// Barratt API - Delivery Appeals, Spend Leakage, Bulk Upload, Commodity Codes
// + Barratt Standalone Module (Category/Analyse BI, Division Performance, Call Off, Data Upload)
import api, { type AxiosResponse, type PaginatedResponse } from './client';

// ============================================================================
// Types
// ============================================================================

export interface FeatureFlags {
  /** Isolated Part Schedule module; per-customer CompanySettings override. Absent = platform default (shown). */
  part_schedule?: boolean;
  delivery_appeals: boolean;
  spend_leakage: boolean;
  bulk_upload: boolean;
  commodity_codes: boolean;
  supplier_stock_levels: boolean;
  switch_call_off_tab: boolean;
  allow_supplier_decline: boolean;
  po_line_schedules: boolean;
  schedule_collaboration: boolean;
  show_forecast_total: boolean;
  /** Forecast grid shows only the weekly horizon (hide month + future columns). */
  forecast_weeks_only: boolean;
  /** Forecast grid + export hide the Supplier Part No column. */
  forecast_hide_supplier_part_no: boolean;
  /** Forecast description comes from the part master, over forecast / PO-line text. */
  forecast_description_from_master: boolean;
  /** PNA-only: enables the "Produmax Export" button on the ASN detail modal. */
  asn_produmax_export: boolean;
  /**
   * CR-6160: paid ASN logistics-tracking module (external courier tracking).
   * Platform-level, Helix-admin only (Platform Admin → Purchasing). Default OFF.
   * When ON, ASN courier + tracking become required and live delivery status is
   * pulled from the tracking provider; when OFF, tracking is fully disabled.
   */
  asn_logistics_tracking: boolean;
  /** Prebuilt spend dashboards (invoice-value charts) — surfaces the
   *  "Spend Dashboard" nav item for tenants with seeded spend reports. */
  spend_dashboard: boolean;
  /** Monthly supplier upload cycle (Supplier Performance + Quality files):
   *  surfaces Data Upload / Supplier Upload History on the customer side and
   *  Monthly Uploads on the supplier side. Default OFF. */
  supplier_uploads: boolean;
  /** Division Performance + Division Score Report nav items (construction tenants). */
  division_scoring: boolean;
  /** Call Off Performance nav item, fed by the monthly Supplier Performance uploads. */
  call_off_performance: boolean;
  /** Current Performance (PO-line dashboard) built from the monthly supplier files instead of PO lines. */
  performance_from_uploads: boolean;
  // Portal mode set by PLATFORM_MODE env var or Admin Setup > Features
  // CR-5334: 'barratt' renamed to neutral 'spend_analytics'; legacy value kept for reads.
  portal_mode?: 'standard' | 'spend_analytics' | 'compliance' | 'customer_portal' | 'barratt';
  // Dashboard tab visibility (CR-4441)
  dashboard_tabs?: Record<string, boolean>;
  supplier_dashboard_tabs?: Record<string, boolean>;
}

export interface DeliveryAppeal {
  id: number;
  appeal_number: string;
  po_line: number;
  po_number?: string;
  po_line_number?: number;
  part_number?: string;
  description?: string;
  appeal_type: string;
  appeal_type_display?: string;
  status: string;
  status_display?: string;
  original_delivery_date: string;
  actual_delivery_date?: string;
  proposed_date?: string;
  reason: string;
  supporting_evidence?: string;
  reviewer_notes?: string;
  rejection_reason?: string;
  created_by?: number;
  created_by_name?: string;
  reviewed_by?: number;
  reviewed_by_name?: string;
  created_at: string;
  updated_at: string;
  submitted_at?: string;
  reviewed_at?: string;
  attachments?: DeliveryAppealAttachment[];
  logs?: DeliveryAppealLog[];
}

export interface DeliveryAppealAttachment {
  id: number;
  appeal: number;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type?: string;
  uploaded_by?: number;
  uploaded_by_name?: string;
  uploaded_at: string;
}

export interface DeliveryAppealLog {
  id: number;
  appeal: number;
  action: string;
  old_status?: string;
  new_status?: string;
  notes?: string;
  performed_by?: number;
  performed_by_name?: string;
  performed_at: string;
}

export interface DeliveryAppealStats {
  total: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
}

export interface SpendLeakageReport {
  id: number;
  report_number: string;
  title: string;
  description?: string;
  status: string;
  status_display?: string;
  period_start: string;
  period_end: string;
  total_spend: number;
  contracted_spend: number;
  leakage_spend: number;
  leakage_percentage: number;
  potential_savings: number;
  /**
   * Split by whether the supplier uploaded its monthly file for the report
   * period. Absent on reports built before the split existed; decimals are
   * serialised as strings by DRF, like the totals above.
   */
  uploaded_spend?: number | string;
  uploaded_leakage?: number | string;
  not_uploaded_spend?: number | string;
  not_uploaded_leakage?: number | string;
  uploaded_supplier_count?: number;
  not_uploaded_supplier_count?: number;
  company?: number;
  company_name?: string;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
  /** No longer embedded for large reports — load through getSpendLeakageItems(). */
  items?: SpendLeakageItem[];
}

export interface SpendLeakageItem {
  id: number;
  report: number;
  po_number?: string;
  po_line?: number;
  supplier?: number;
  supplier_name?: string;
  supplier_display?: string;
  category: string;
  category_display?: string;
  description: string;
  spend_amount: number;
  contracted_price?: number;
  variance_amount: number;
  transaction_date: string;
  notes?: string;
  action_taken?: string;
  /** Whether the supplier uploaded its monthly file for the period; null when unknown / no supplier. */
  supplier_uploaded?: boolean | null;
}

/** One month of the running 12-month leakage series; null values = no report for that month yet. */
export interface SpendLeakageTrendMonth {
  period: string;          // 'YYYY-MM'
  label: string;           // 'Sep 2025'
  report_id: number | null;
  total_spend: number | null;
  leakage_spend: number | null;
  uploaded_leakage: number | null;
  not_uploaded_leakage: number | null;
  /** Leakage from months before upload tracking began (no uploaded/not-uploaded split). */
  untracked_leakage: number | null;
  leakage_percentage: number | null;
}

export interface SpendLeakageTrend {
  months: SpendLeakageTrendMonth[];
}

export interface SpendLeakageDashboard {
  total_leakage: number;
  uploaded_leakage?: number;
  not_uploaded_leakage?: number;
  report_count: number;
  /** Newest published report with the longest window (the rolling last-12-months one). */
  headline_report?: SpendLeakageReport | null;
  recent_reports: SpendLeakageReport[];
}

export interface CommodityCode {
  id: number;
  code: string;
  name: string;
  description?: string;
  parent?: number;
  parent_name?: string;
  level: number;
  children_count?: number;
  full_path?: string;
  company?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  children?: CommodityCode[];
}

// ============================================================================
// Dataset Types (Barratt BI System)
// ============================================================================

export interface Dataset {
  id: number;
  name: string;
  ds_type: 'invoice' | 'supplier_performance' | 'division_performance' | 'quality' | 'custom';
  ds_type_display?: string;
  data_source: 'upload' | 'api' | 'manual';
  data_source_display?: string;
  description?: string;
  is_enabled: boolean;
  company?: number;
  company_name?: string;
  owner?: number;
  owner_name?: string;
  icon_content?: string;
  icon_extension?: string;
  import_number?: number;
  last_data_update?: string;
  fields?: DatasetField[];
  record_count?: number;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface DatasetField {
  id: number;
  dataset: number;
  bind_field: string;
  title: string;
  field_type:
    | 'string'
    | 'integer'
    | 'decimal'
    | 'date'
    | 'datetime'
    | 'boolean'
    | 'lookup'
    | 'percentage'
    | 'currency'
    | 'time'
    | 'document'
    | 'link';
  field_type_display?: string;
  is_enabled: boolean;
  is_key_field: boolean;
  is_filter: boolean;
  filter_type: 'none' | 'dropdown' | 'search' | 'date_range' | 'number_range';
  filter_type_display?: string;
  is_default_view: boolean;
  is_sortable: boolean;
  align_type: 'left' | 'center' | 'right';
  use_thousand_separator: boolean;
  decimal_places: number;
  date_format: string;
  char_length?: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface DatasetRecord {
  id: number;
  data: Record<string, unknown>;
  created_at: string;
}

// ============================================================================
// Analyse Category & Dashboard Types (Barratt Standalone Module)
// ============================================================================

export interface AnalyseCategory {
  id: number;
  name: string;
  ds_type: 'standard' | 'barratt';
  ds_type_display?: string;
  hint?: string;
  company?: number;
  sort_order: number;
  icon_content?: string;
  icon_extension?: string;
  icon_version?: number;
  original_category?: number;
  created_by?: number;
  created_by_name?: string;
  dashboard_count?: number;
  created_at: string;
  updated_at: string;
}

export interface AnalyseDashboard {
  id: number;
  name: string;
  description?: string;
  is_enabled: boolean;
  category?: number;
  category_name?: string;
  dataset?: number;
  dataset_name?: string;
  is_filter_board: boolean;
  is_advanced_filter_board: boolean;
  filter_field?: number;
  shared_with: 'private' | 'company' | 'company_and_suppliers';
  shared_with_display?: string;
  level: 1 | 2 | 3;
  level_display?: string;
  download_lock: boolean;
  company?: number;
  sort_order: number;
  app_code?: string;
  indicator_count?: number;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface DashboardIndicator {
  id: number;
  category: number;
  category_name?: string;
  category_dashboard?: number;
  name: string;
  chart_type: 'bar' | 'line' | 'pie' | 'donut' | 'waterfall' | 'heatmap' | 'summary' | 'grid' | 'kpi' | 'indicator' | 'geolocation' | 'markup';
  chart_type_display?: string;
  hint?: string;
  config: Record<string, unknown>;
  dataset?: number;
  dataset_name?: string;
  shape_key?: number;
  icon_content?: string;
  icon_extension?: string;
  icon_version?: number;
  original_indicator_id?: number;
  original_dashboard_id?: number;
  company?: number;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
}

// ============================================================================
// Import/Upload Types
// ============================================================================

export interface ImportSummary {
  id: number;
  dataset: number;
  dataset_name?: string;
  template?: number;
  template_display?: string;
  company?: number;
  company_name?: string;
  user?: number;
  user_name?: string;
  total_records: number;
  total_pages: number;
  imported_pages: number;
  imported_pages_success: number;
  page_size: number;
  is_success: boolean;
  is_complete: boolean;
  completed_by_manual: boolean;
  is_create_fields: boolean;
  summary_type: 1 | 14;
  summary_type_display?: string;
  progress_percentage?: number;
  log_count?: number;
  total_added?: number;
  total_failed?: number;
  has_errors?: boolean;
  created_at: string;
  completed_at?: string;
  /** Upload-cycle row this import satisfied (Supplier Performance / Quality files only). */
  tracking?: SupplierUploadTracking | null;
  /** Name of the uploaded file (backend ImportSummary.file_name). */
  file_name?: string;
  /** Template the file was read against ('Supplier Performance', 'Quality', …). */
  template_name?: string;
  /** Month a monthly file reports on, e.g. 'Aug 2026'; null for other imports. */
  covers_period_label?: string | null;
  /** Supplier whose month this file satisfied; null for the customer's own imports. */
  supplier_name?: string | null;
  /**
   * Why the file was rejected, sent with the upload response itself — the
   * exceptions endpoint is scoped to the caller's active company, which is not
   * where a supplier's upload is saved. Capped; `exception_counts` is not.
   */
  exceptions?: ImportException[];
  exception_counts?: { error: number; warning: number };
  /** Distinct data rows that failed validation. */
  rejected_rows?: number;
  /** Rows kept from a rejected file — always 0 for a monthly obligation file. */
  rows_kept?: number;
}

export interface ImportLog {
  id: number;
  import_summary: number;
  company?: number;
  company_name?: string;
  user?: number;
  user_name?: string;
  page_index: number;
  is_success: boolean;
  import_count: number;
  added: number;
  force_added: number;
  updated: number;
  skipped: number;
  failed: number;
  key_field_empty: number;
  repeat: number;
  deleted: number;
  empty_row: number;
  merge_field_not_found: number;
  calculated_failed: number;
  description?: string;
  push_time?: string;
  created_at: string;
  finish_time?: string;
  exception_count?: number;
  success_rate?: number;
  exceptions?: ImportException[];
}

export interface ImportException {
  id: number;
  import_log: number;
  row_number: number;
  column_name?: string;
  severity: 'error' | 'warning' | 'info';
  severity_display?: string;
  error_code?: string;
  message: string;
  raw_data: Record<string, string>;
  created_at: string;
}

export interface ImportTemplate {
  id: number;
  name: string;
  description: string;
  columns: string[];
  required_columns: string[];
}

/** Template ids the monthly supplier upload cycle tracks. */
export const SUPPLIER_PERFORMANCE_TEMPLATE_ID = 2;
export const QUALITY_TEMPLATE_ID = 4;

// ============================================================================
// Division Performance Types
// ============================================================================

/** One cell of the division score pivot (a supplier scored by one division). */
export interface DivisionScoreReportCell {
  score: number;
  display_score: number;
  comments: string;
}

/** One pivot row: a supplier with its per-division scores and averages. */
export interface DivisionScoreReportRow {
  supplier: string;
  scores: Record<string, DivisionScoreReportCell>;
  average_score: number;
  average_display_score: number;
}

export interface DivisionScoreReportResponse {
  year: string | null;
  quarter: string | null;
  data: DivisionScoreReportRow[];
}

export interface DivisionPerformanceScore {
  id: number;
  customer: number;
  customer_name?: string;
  supplier: number;
  supplier_name?: string;
  performance_date: string;
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  quarter_display?: string;
  year: number;
  score: 1 | 2 | 3 | 4 | 5;
  display_score?: number; // score * 20 (20/40/60/80/100)
  comments?: string;
  include_in_division_score: boolean;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface QuarterCompletion {
  id: number;
  customer: number;
  customer_name?: string;
  quarter: string;
  year: number;
  is_completed: boolean;
  completed_at?: string;
  completed_by?: number;
  completed_by_name?: string;
}

export interface QuarterInfo {
  value: string;
  label: string;
}

export interface ScoreReportRow {
  supplier: string;
  scores: Record<string, { score: number; display_score: number; comments?: string }>;
  average_score: number;
  average_display_score: number;
}

// ============================================================================
// Call Off Performance Types
// ============================================================================

export interface CallOffPerformanceData {
  id: number;
  dataset: number;
  dataset_name?: string;
  customer: number;
  customer_name?: string;
  supplier: number;
  supplier_name?: string;
  required_date: string;
  call_off_date: string;
  week_interval: number;
  additional_data: Record<string, unknown>;
  created_at: string;
}

export interface CallOffAggregatedData {
  columns: string[];
  data: Array<Record<string, string | number>>;
  max_week: number;
}

// ============================================================================
// Supplier Upload Tracking Types
// ============================================================================

export type SupplierUploadDatasetType = 'supplier_performance' | 'quality';

export type SupplierUploadStatus = 'pending' | 'uploaded' | 'overdue';

/** One supplier × one dataset × one monthly period. */
export interface SupplierUploadTracking {
  id: number;
  customer: number;
  customer_name?: string;
  supplier: number;
  supplier_name?: string;
  supplier_email?: string;
  supplier_contact_name?: string;
  dataset_type: SupplierUploadDatasetType;
  dataset_type_display: string;
  /** 'YYYY-MM'. Optional so older rows still render — derive from period_start when absent. */
  period?: string;
  period_label?: string;
  period_start: string;
  period_end: string;
  /** ISO date the file is due by. Null when the customer's schedule sets none. */
  due_date: string | null;
  status: SupplierUploadStatus;
  status_display?: string;
  last_upload?: number;
  uploaded_at?: string;
  file_name?: string;
  records_count: number;
  otif_pct: number | null;
  ncr_count: number | null;
  last_reminder_at: string | null;
  reminder_count: number;
  days_since_upload?: number;
  created_at: string;
  updated_at: string;
}

export interface UploadSummary {
  period: string;
  period_label: string;
  due_date: string | null;
  total_suppliers: number;
  /** Rows due in the period: suppliers × the datasets each owes. */
  total_obligations: number;
  /** File counts: one supplier owing two files counts twice. */
  uploaded: number;
  pending: number;
  overdue: number;
  uploaded_pct: number;
  /** Supplier counts — a supplier is uploaded only once every file it owes is in. */
  suppliers_uploaded: number;
  suppliers_pending: number;
  suppliers_overdue: number;
  suppliers_uploaded_pct: number;
}

export interface UploadPeriod {
  /** 'YYYY-MM' */
  period: string;
  label: string;
  due_date: string;
  is_current: boolean;
}

export interface ChaseResult {
  sent: number;
  skipped: number;
  message: string;
}

// ============================================================================
// API Methods
// ============================================================================

export const barrattAPI = {
  // Feature Flags
  getFeatureFlags: (): Promise<AxiosResponse<FeatureFlags>> =>
    api.get('/barratt/feature-flags/'),

  // ============================
  // Delivery Appeals
  // ============================
  getDeliveryAppeals: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<DeliveryAppeal>>> =>
    api.get('/barratt/delivery-appeals/', { params }),

  getDeliveryAppeal: (id: number): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.get(`/barratt/delivery-appeals/${id}/`),

  createDeliveryAppeal: (data: Partial<DeliveryAppeal>): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.post('/barratt/delivery-appeals/', data),

  updateDeliveryAppeal: (id: number, data: Partial<DeliveryAppeal>): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.patch(`/barratt/delivery-appeals/${id}/`, data),

  deleteDeliveryAppeal: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/delivery-appeals/${id}/`),

  submitDeliveryAppeal: (id: number): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.post(`/barratt/delivery-appeals/${id}/submit/`),

  reviewDeliveryAppeal: (id: number, data: {
    action: 'approve' | 'reject';
    reviewer_notes?: string;
    rejection_reason?: string;
  }): Promise<AxiosResponse<DeliveryAppeal>> =>
    api.post(`/barratt/delivery-appeals/${id}/review/`, data),

  uploadDeliveryAppealAttachment: (id: number, formData: FormData): Promise<AxiosResponse<DeliveryAppealAttachment>> =>
    api.post(`/barratt/delivery-appeals/${id}/upload_attachment/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  getDeliveryAppealStats: (): Promise<AxiosResponse<DeliveryAppealStats>> =>
    api.get('/barratt/delivery-appeals/stats/'),

  // ============================
  // Spend Leakage
  // ============================
  getSpendLeakageReports: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<SpendLeakageReport>>> =>
    api.get('/barratt/spend-leakage/', { params }),

  getSpendLeakageReport: (id: number): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.get(`/barratt/spend-leakage/${id}/`),

  createSpendLeakageReport: (data: {
    title: string;
    description?: string;
    period_start: string;
    period_end: string;
  }): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.post('/barratt/spend-leakage/', data),

  updateSpendLeakageReport: (id: number, data: Partial<SpendLeakageReport>): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.patch(`/barratt/spend-leakage/${id}/`, data),

  deleteSpendLeakageReport: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/spend-leakage/${id}/`),

  publishSpendLeakageReport: (id: number): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.post(`/barratt/spend-leakage/${id}/publish/`),

  archiveSpendLeakageReport: (id: number): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.post(`/barratt/spend-leakage/${id}/archive/`),

  addSpendLeakageItem: (reportId: number, data: Partial<SpendLeakageItem>): Promise<AxiosResponse<SpendLeakageItem>> =>
    api.post(`/barratt/spend-leakage/${reportId}/add_item/`, data),

  recalculateSpendLeakageReport: (id: number): Promise<AxiosResponse<SpendLeakageReport>> =>
    api.post(`/barratt/spend-leakage/${id}/recalculate/`),

  getSpendLeakageTrend: (): Promise<AxiosResponse<SpendLeakageTrend>> =>
    api.get('/barratt/spend-leakage/trend/'),

  getSpendLeakageDashboard: (): Promise<AxiosResponse<SpendLeakageDashboard>> =>
    api.get('/barratt/spend-leakage/dashboard/'),

  /** Paginated items for one report; `uploaded` splits by supplier upload status. */
  getSpendLeakageItems: (
    reportId: number,
    params?: { uploaded?: boolean; page?: number; page_size?: number },
  ): Promise<AxiosResponse<PaginatedResponse<SpendLeakageItem>>> =>
    api.get(`/barratt/spend-leakage/${reportId}/items/`, { params }),

  // ============================
  // Commodity Codes
  // ============================
  getCommodityCodes: (params?: Record<string, string | number | boolean>): Promise<AxiosResponse<PaginatedResponse<CommodityCode>>> =>
    api.get('/barratt/commodity-codes/', { params }),

  getCommodityCode: (id: number): Promise<AxiosResponse<CommodityCode>> =>
    api.get(`/barratt/commodity-codes/${id}/`),

  createCommodityCode: (data: Partial<CommodityCode>): Promise<AxiosResponse<CommodityCode>> =>
    api.post('/barratt/commodity-codes/', data),

  updateCommodityCode: (id: number, data: Partial<CommodityCode>): Promise<AxiosResponse<CommodityCode>> =>
    api.patch(`/barratt/commodity-codes/${id}/`, data),

  deleteCommodityCode: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/commodity-codes/${id}/`),

  getCommodityCodeTree: (): Promise<AxiosResponse<CommodityCode[]>> =>
    api.get('/barratt/commodity-codes/tree/'),

  getCommodityCodeChildren: (id: number): Promise<AxiosResponse<CommodityCode[]>> =>
    api.get(`/barratt/commodity-codes/${id}/children/`),

  // ============================
  // Datasets (Barratt BI System)
  // ============================
  getDatasets: (params?: Record<string, string | number | boolean>): Promise<AxiosResponse<PaginatedResponse<Dataset>>> =>
    api.get('/barratt/datasets/', { params }),

  getDataset: (id: number): Promise<AxiosResponse<Dataset>> =>
    api.get(`/barratt/datasets/${id}/`),

  createDataset: (data: Partial<Dataset>): Promise<AxiosResponse<Dataset>> =>
    api.post('/barratt/datasets/', data),

  updateDataset: (id: number, data: Partial<Dataset>): Promise<AxiosResponse<Dataset>> =>
    api.patch(`/barratt/datasets/${id}/`, data),

  deleteDataset: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/datasets/${id}/`),

  getDatasetFields: (datasetId: number): Promise<AxiosResponse<DatasetField[]>> =>
    api.get(`/barratt/datasets/${datasetId}/fields/`),

  addDatasetField: (datasetId: number, data: Partial<DatasetField>): Promise<AxiosResponse<DatasetField>> =>
    api.post(`/barratt/datasets/${datasetId}/add_field/`, data),

  getDatasetRecords: (datasetId: number, params?: { page?: number; page_size?: number }): Promise<AxiosResponse<{
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
    results: DatasetRecord[];
  }>> =>
    api.get(`/barratt/datasets/${datasetId}/records/`, { params }),

  // ============================
  // Analyse Categories (Barratt Standalone)
  // ============================
  getCategories: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<AnalyseCategory>>> =>
    api.get('/barratt/categories/', { params }),

  getCategory: (id: number): Promise<AxiosResponse<AnalyseCategory>> =>
    api.get(`/barratt/categories/${id}/`),

  createCategory: (data: { name: string; hint?: string; sort_order?: number }): Promise<AxiosResponse<AnalyseCategory>> =>
    api.post('/barratt/categories/', data),

  updateCategory: (id: number, data: Partial<AnalyseCategory>): Promise<AxiosResponse<AnalyseCategory>> =>
    api.patch(`/barratt/categories/${id}/`, data),

  deleteCategory: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/categories/${id}/`),

  renameCategory: (id: number, name: string): Promise<AxiosResponse<AnalyseCategory>> =>
    api.post(`/barratt/categories/${id}/rename/`, { name }),

  // ============================
  // Analyse Dashboards
  // ============================
  getDashboards: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<AnalyseDashboard>>> =>
    api.get('/barratt/dashboards/', { params }),

  getDashboard: (id: number): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.get(`/barratt/dashboards/${id}/`),

  createDashboard: (data: Partial<AnalyseDashboard>): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.post('/barratt/dashboards/', data),

  updateDashboard: (id: number, data: Partial<AnalyseDashboard>): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.patch(`/barratt/dashboards/${id}/`, data),

  deleteDashboard: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/dashboards/${id}/`),

  shareDashboard: (id: number, sharedWith: string): Promise<AxiosResponse<AnalyseDashboard>> =>
    api.post(`/barratt/dashboards/${id}/share/`, { shared_with: sharedWith }),

  // ============================
  // Dashboard Indicators
  // ============================
  getIndicators: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<DashboardIndicator>>> =>
    api.get('/barratt/indicators/', { params }),

  getIndicator: (id: number): Promise<AxiosResponse<DashboardIndicator>> =>
    api.get(`/barratt/indicators/${id}/`),

  createIndicator: (data: Partial<DashboardIndicator>): Promise<AxiosResponse<DashboardIndicator>> =>
    api.post('/barratt/indicators/', data),

  updateIndicator: (id: number, data: Partial<DashboardIndicator>): Promise<AxiosResponse<DashboardIndicator>> =>
    api.patch(`/barratt/indicators/${id}/`, data),

  deleteIndicator: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/indicators/${id}/`),

  // ============================
  // Division Performance
  // ============================
  getDivisionPerformanceScores: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<DivisionPerformanceScore>>> =>
    api.get('/barratt/division-performance/', { params }),

  getDivisionPerformanceScore: (id: number): Promise<AxiosResponse<DivisionPerformanceScore>> =>
    api.get(`/barratt/division-performance/${id}/`),

  getDivisionPerformanceYears: (): Promise<AxiosResponse<{ years: number[] }>> =>
    api.get('/barratt/division-performance/available_years/'),

  // Pivot report: suppliers as rows, divisions as columns, plus averages.
  getDivisionScoreReport: (quarter: string, year: string | number): Promise<AxiosResponse<DivisionScoreReportResponse>> =>
    api.get('/barratt/division-performance/score_report/', { params: { quarter, year } }),

  getDivisionPerformanceQuarters: (): Promise<AxiosResponse<{ quarters: QuarterInfo[] }>> =>
    api.get('/barratt/division-performance/available_quarters/'),

  getCurrentQuarter: (): Promise<AxiosResponse<{ quarter: string; year: number }>> =>
    api.get('/barratt/division-performance/current_quarter/'),

  saveDivisionPerformanceScore: (data: {
    supplier_id: number;
    score: number;
    comments?: string;
    quarter: string;
    year: number;
  }): Promise<AxiosResponse<DivisionPerformanceScore>> =>
    api.post('/barratt/division-performance/save_score/', data),

  completeQuarter: (quarter: string, year: number): Promise<AxiosResponse<QuarterCompletion>> =>
    api.post('/barratt/division-performance/complete_quarter/', { quarter, year }),

  reopenQuarter: (quarter: string, year: number): Promise<AxiosResponse<QuarterCompletion>> =>
    api.post('/barratt/division-performance/reopen_quarter/', { quarter, year }),

  getQuarterStatus: (quarter: string, year: number): Promise<AxiosResponse<{
    is_completed: boolean;
    quarter: string;
    year: number;
    completed_at?: string;
    completed_by?: string;
  }>> =>
    api.get('/barratt/division-performance/quarter_status/', { params: { quarter, year } }),

  getScoreReport: (params?: { year?: number; quarter?: string }): Promise<AxiosResponse<{
    year?: number;
    quarter?: string;
    data: ScoreReportRow[];
  }>> =>
    api.get('/barratt/division-performance/score_report/', { params }),

  // ============================
  // Call Off Performance
  // ============================
  getCallOffPerformance: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<CallOffPerformanceData>>> =>
    api.get('/barratt/calloff-performance/', { params }),

  getCallOffColumns: (): Promise<AxiosResponse<{ columns: string[]; max_week: number }>> =>
    api.get('/barratt/calloff-performance/columns/'),

  getCallOffAggregatedData: (params?: Record<string, string | number>): Promise<AxiosResponse<CallOffAggregatedData>> =>
    api.get('/barratt/calloff-performance/aggregated_data/', { params }),

  // ============================
  // Supplier Upload Tracking
  // ============================
  getSupplierUploads: (params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResponse<SupplierUploadTracking>>> =>
    api.get('/barratt/supplier-uploads/', { params }),

  getSupplierUpload: (id: number): Promise<AxiosResponse<SupplierUploadTracking>> =>
    api.get(`/barratt/supplier-uploads/${id}/`),

  getSuppliersUploaded: (period?: string): Promise<AxiosResponse<SupplierUploadTracking[]>> =>
    api.get('/barratt/supplier-uploads/uploaded/', { params: period ? { period } : undefined }),

  getSuppliersNotUploaded: (period?: string): Promise<AxiosResponse<SupplierUploadTracking[]>> =>
    api.get('/barratt/supplier-uploads/not_uploaded/', { params: period ? { period } : undefined }),

  getUploadSummary: (period?: string): Promise<AxiosResponse<UploadSummary>> =>
    api.get('/barratt/supplier-uploads/summary/', { params: period ? { period } : undefined }),

  /** Last 12 monthly periods, newest first. */
  getUploadPeriods: (): Promise<AxiosResponse<{ periods: UploadPeriod[] }>> =>
    api.get('/barratt/supplier-uploads/periods/'),

  /** Email a reminder to the given rows, or to every not-uploaded row in the period when ids is omitted. */
  chaseSupplierUploads: (data: { ids?: number[]; period?: string }): Promise<AxiosResponse<ChaseResult>> =>
    api.post('/barratt/supplier-uploads/chase/', data),

  /** The logged-in supplier's own rows, newest period first. */
  getMyUploadObligations: (periods = 3): Promise<AxiosResponse<{ obligations: SupplierUploadTracking[] }>> =>
    api.get('/barratt/supplier-uploads/my_obligations/', { params: { periods } }),

  // ============================
  // Import Summaries (Barratt Data Upload)
  // ============================
  getImportSummaries: (params?: Record<string, string | number | boolean>): Promise<AxiosResponse<PaginatedResponse<ImportSummary>>> =>
    api.get('/barratt/import-summaries/', { params }),

  getImportSummary: (id: number): Promise<AxiosResponse<ImportSummary>> =>
    api.get(`/barratt/import-summaries/${id}/`),

  getImportSummaryLogs: (id: number): Promise<AxiosResponse<ImportLog[]>> =>
    api.get(`/barratt/import-summaries/${id}/logs/`),

  getImportSummaryExceptions: (id: number): Promise<AxiosResponse<ImportException[]>> =>
    api.get(`/barratt/import-summaries/${id}/exceptions/`),

  markImportComplete: (id: number): Promise<AxiosResponse<ImportSummary>> =>
    api.post(`/barratt/import-summaries/${id}/mark_complete/`),

  getImportTemplates: (): Promise<AxiosResponse<{ templates: ImportTemplate[] }>> =>
    api.get('/barratt/import-summaries/templates/'),

  uploadFile: (formData: FormData): Promise<AxiosResponse<ImportSummary>> =>
    api.post('/barratt/import-summaries/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  deleteImportSummary: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/barratt/import-summaries/${id}/`),
};
